/**
 * Shared tokens for every "why is this blocked" surface: the locked-feature
 * card (`FeatureGateBlock`) and the page/compact banner (`FeatureBanner`).
 *
 * Source of truth: the "Banner system" section of `Modal System.dc.html` in the
 * Inkfree design project. Only two tones exist here — `upgrade` (not on the
 * current plan) and `off` (an admin turned it off) — because that's the whole
 * of the app's real feature-gate data model (`FeatureState` in
 * `hooks/use-feature.ts`). The mock's red "service disruption" tone has no
 * backing state (no outage/degraded/scheduled-maintenance concept exists) and
 * is intentionally not built.
 *
 * `upgrade` reuses `AppModal`'s new `amber` tone so the lock icon, tile tint
 * and button colour are pixel-identical between the modal and these surfaces.
 * `off` reuses the existing `neutral` tone for the same reason — and because
 * `#6D52A2` already clears 4.5:1 on white, it needs no WCAG substitution split
 * the way `upgrade`'s `#B47207` does (see `AppModal.tokens.ts`).
 */
import appModalTokens from "global/components/AppModal/AppModal.tokens";
import emptyStateTokens, {
  EMPTY_PHONE_QUERY,
} from "global/components/EmptyState/EmptyState.tokens";
import { signer } from "theme/colors";

const m = appModalTokens;
const es = emptyStateTokens;

export type FeatureGateTone = "upgrade" | "off";

export const featureGateTokens = {
  tone: {
    upgrade: {
      /** Card / banner surface. */
      bg: signer.orange.faint,
      border: "#F5E3BE",
      /** Icon tile — decorative, only needs the 3:1 non-text bar. */
      tile: m.tone.amber.tile,
      icon: m.tone.amber.glyph,
      /** Badge fill + any text-bearing element (AA-safe darkened gold). */
      badgeBg: m.tone.amber.tile,
      text: m.amber.fill,
      textHover: m.amber.activeFill,
    },
    off: {
      bg: signer.purple.faint,
      border: "#E2DAF1",
      tile: m.tone.neutral.tile,
      icon: m.tone.neutral.glyph,
      badgeBg: m.tone.neutral.tile,
      text: m.tone.neutral.glyph,
      textHover: "#5A4488",
    },
  } as Record<
    FeatureGateTone,
    {
      bg: string;
      border: string;
      tile: string;
      icon: string;
      badgeBg: string;
      text: string;
      textHover: string;
    }
  >,

  color: {
    ink: m.color.ink,
    body: m.color.body,
    muted: m.color.muted,
    hairline: m.color.hairline,
    innerSurface: m.color.innerSurface,
  },

  /**
   * The full-page block and the framed empty state land in the same slot on the
   * same screens — often one after the other as a plan changes — so the block
   * borrows the empty state's geometry outright instead of keeping a near-miss
   * copy of it. Read from `EmptyState.tokens` so the two cannot drift again.
   * The `inline` variant keeps its own, tighter numbers: it sits inside a panel
   * that already has padding, not on the bare page.
   */
  layout: {
    padY: es.well.paddingY.full,
    padX: es.well.paddingX.full,
    gap: es.well.gap.full,
    phoneQuery: EMPTY_PHONE_QUERY,
    phonePadY: es.well.phonePaddingY,
    phonePadX: es.well.phonePaddingX,
    phoneGap: es.well.phoneGap,
    titleFontSize: es.title.fontSize.full,
    titleFontSizePhone: es.title.fontSize.phone,
    bodyMaxWidth: es.body.maxWidth.full,
    bodyLineHeight: es.body.lineHeight.full,
    tileSize: es.tile.size.full,
    tileRadius: es.tile.radius.full,
    tileSizePhone: es.tile.size.phone,
    tileRadiusPhone: es.tile.radius.phone,
  },

  radius: {
    card: "13px",
    /** Matches the framed empty state — both are "this page has no content". */
    cardOuter: "16px",
    tile: "11px",
    tileLg: "13px",
    badge: "999px",
  },
} as const;

export default featureGateTokens;
